/* Batch 33 · S12 — firm settings that feed the auditor's report: ethics requirements ({{ethics_requirements}}) and the practice
   location ({{auditor_location}} / {{practitioner_location}}). Saved through the existing PATCH /organization (Admin / Partner).
   The report screen links here (#firm-report-settings) when either is blank. */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { errText } from "@/lib/i18n/1250_b34g";   // Batch 34 · P51-S9
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export default function FirmReportSettingsCard({ canEdit }) {
  const { t, lang } = useI18n();
  const [vals, setVals] = useState({ ethics_requirements: "", location: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!canEdit) return;
    api.get("/organization", { _silent: true })
      .then((r) => setVals({ ethics_requirements: r.data?.ethics_requirements || "", location: r.data?.location || r.data?.city || "" }))
      .catch(() => {});
  }, [canEdit]);
  useEffect(() => {   // the report screen's link opens /settings#firm-report-settings
    if (window.location.hash === "#firm-report-settings") {
      const el = document.getElementById("firm-report-settings");
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "start" });
    }
  }, []);
  if (!canEdit) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/organization", { ethics_requirements: vals.ethics_requirements.trim(), location: vals.location.trim() },
                      { _silent: true });
      toast.success(t("b33frs.saved"));
    } catch (e) { toast.error(errText(e, lang, t("b33frs.failed"))); }   // Batch 34 · P51-S9: coded EN + AR (was "Failed")
    finally { setSaving(false); }
  };
  return (
    <Card id="firm-report-settings" data-testid="firm-report-settings">
      <CardHeader>
        <CardTitle className="text-base">{t("b33frs.title")}</CardTitle>
        <p className="text-[11px] text-muted-foreground">{t("b33frs.intro")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>{t("b33frs.ethics")}</Label>
          <Textarea value={vals.ethics_requirements} onChange={(e) => setVals({ ...vals, ethics_requirements: e.target.value })}
                    className="mt-1 text-sm" data-testid="frs-ethics" />
          <div className="text-[10px] text-muted-foreground">{t("b33frs.ethicsHelp")}</div>
        </div>
        <div>
          <Label>{t("b33frs.location")}</Label>
          <Input value={vals.location} onChange={(e) => setVals({ ...vals, location: e.target.value })} data-testid="frs-location" />
          <div className="text-[10px] text-muted-foreground">{t("b33frs.locationHelp")}</div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="frs-save">{t("common.save")}</Button>
      </CardContent>
    </Card>
  );
}
